export type BrowserSpeechResult = { isFinal: boolean; 0?: { transcript?: string } };
export type BrowserSpeechEvent = { resultIndex: number; results: ArrayLike<BrowserSpeechResult> };
export type BrowserSpeechRecognition = {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  onresult: ((event: BrowserSpeechEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void; stop: () => void; abort: () => void;
};
export type BrowserSpeechConstructor = new () => BrowserSpeechRecognition;
export type BrowserSpeechSchedule = (callback: () => void, delayMs: number) => () => void;

const scheduleBrowserSpeech: BrowserSpeechSchedule = (callback, delayMs) => {
  const timer = setTimeout(callback, delayMs);
  return () => clearTimeout(timer);
};

export function browserSpeechConstructor(): BrowserSpeechConstructor | null {
  const candidate = window as Window & { SpeechRecognition?: BrowserSpeechConstructor; webkitSpeechRecognition?: BrowserSpeechConstructor };
  return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null;
}

/** Owns recognition and its timeouts so a stale browser event cannot reach the form. */
export class BrowserSpeechController {
  private recognition: BrowserSpeechRecognition | null = null;
  private cancelled = false;
  private settled = false;
  private stopping = false;
  private finalText = "";
  private stopFailure = "Konuşma tamamlanamadı. Yerel sesle yeniden deneyin veya klavyeyle girin.";
  private cancelFirstResult: (() => void) | null = null;
  private cancelHardLimit: (() => void) | null = null;
  private cancelStopLimit: (() => void) | null = null;
  constructor(
    private readonly create: BrowserSpeechConstructor,
    private readonly callbacks: { interim: (text: string) => void; final: (text: string) => void; failure: (message: string) => void },
    private readonly schedule: BrowserSpeechSchedule = scheduleBrowserSpeech,
  ) {}
  start() {
    if (this.recognition || this.settled || this.cancelled) return;
    try {
      const recognition = new this.create();
      this.recognition = recognition;
      recognition.lang = "tr-TR"; recognition.continuous = false; recognition.interimResults = true; recognition.maxAlternatives = 1;
      recognition.onresult = (event) => this.handleResult(event);
      recognition.onerror = (event) => this.fail(browserSpeechError(event.error));
      recognition.onend = () => this.handleEnd();
      // Install limits before native start; some engines synchronously report a failure.
      this.cancelFirstResult = this.schedule(() => this.stop("Tarayıcı zamanında sonuç vermedi. Yerel sesle yeniden deneyin veya klavyeyle girin."), 10_000);
      this.cancelHardLimit = this.schedule(() => this.stop("Tarayıcı en fazla 45 saniye dinleyebilir. Yerel sesle yeniden deneyin veya klavyeyle girin."), 45_000);
      recognition.start();
    } catch { this.fail("Tarayıcı ses tanımayı başlatamadı. Yerel sesle yeniden deneyin veya klavyeyle girin."); }
  }
  stop(message = "Tarayıcı zamanında sonucu kapatamadı. Yerel sesle yeniden deneyin veya klavyeyle girin.") {
    if (!this.recognition || this.cancelled || this.settled || this.stopping) return;
    this.stopping = true; this.stopFailure = message; this.clearTimer("first"); this.clearTimer("hard");
    this.cancelStopLimit = this.schedule(() => this.fail(this.stopFailure), 3_000);
    try { this.recognition.stop(); } catch { this.fail("Tarayıcı sesi durduramadı. Yerel sesle yeniden deneyin veya klavyeyle girin."); }
  }
  cancel() {
    if (this.cancelled) return;
    this.cancelled = true; this.settled = true; this.clearTimers(); this.detach(true);
  }
  private handleResult(event: BrowserSpeechEvent) {
    if (this.cancelled || this.settled) return;
    this.clearTimer("first");
    const final: string[] = [], interim: string[] = [];
    for (let index = 0; index < event.results.length; index += 1) {
      const result = event.results[index]; const transcript = result[0]?.transcript?.trim() ?? "";
      if (result.isFinal) final.push(transcript); else interim.push(transcript);
    }
    this.finalText = final.filter(Boolean).join(" ");
    if (interim.length) this.callbacks.interim(interim.filter(Boolean).join(" "));
    if (this.finalText) this.stop();
  }
  private handleEnd() {
    if (this.cancelled || this.settled) return;
    if (this.finalText.trim()) {
      this.settled = true; this.clearTimers(); this.detach(false); this.callbacks.final(this.finalText.trim());
    } else this.fail(this.stopFailure);
  }
  private fail(message: string) {
    if (this.cancelled || this.settled) return;
    this.settled = true; this.clearTimers(); this.detach(true); this.callbacks.failure(message);
  }
  private clearTimer(which: "first" | "hard" | "stop") {
    const field = which === "first" ? "cancelFirstResult" : which === "hard" ? "cancelHardLimit" : "cancelStopLimit";
    this[field]?.(); this[field] = null;
  }
  private clearTimers() { this.clearTimer("first"); this.clearTimer("hard"); this.clearTimer("stop"); }
  private detach(abort: boolean) {
    const recognition = this.recognition; this.recognition = null;
    if (!recognition) return;
    recognition.onresult = null; recognition.onerror = null; recognition.onend = null;
    try { if (abort) recognition.abort(); } catch { /* Native engines may already be terminal. */ }
  }
}

function browserSpeechError(code: string | undefined) {
  if (code === "not-allowed" || code === "service-not-allowed") return "Mikrofon izni verilmedi. Yerel sesle yeniden deneyin veya klavyeyle girin.";
  if (code === "no-speech") return "Konuşma duyulamadı. Yerel sesle yeniden deneyin veya klavyeyle girin.";
  return "Tarayıcı sesi çözemedi. Yerel sesle yeniden deneyin veya klavyeyle girin.";
}
